import SearchHelpPopover from "./SearchHelpPopover";
import { FILTER_MODE, DATE_MODE, SORT_ORDER } from "./constants";
import { resolveLocationIconEmoji } from "./searchSupport";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import InlineSearch from "components/common/InlineSearch";
import * as labels from "config.labels";
import { SEARCH_HELP_POPOVER } from "flags";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as userSelectors from "store/selectors/user/userSelectors";

const SearchBar = forwardRef((props, ref) => {
  const {
    activeFilter,
    activeDateMode,
    activeSort,
    isFetching,
    onChangeSearch,
    onChangeFilter,
    onChangeDateMode,
    onChangeSort,
    locationConfig,
    searchConfig,
  } = props;

  const searchRef = useRef();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const [helpAnchorEl, setHelpAnchorEl] = useState(null);

  const isVisible = useSelector(userSelectors.selectIsVisible);
  const searchGuide = searchConfig?.search_guide;
  const showHelpPopover = isVisible(SEARCH_HELP_POPOVER) && searchGuide;

  const getSortOptions = () => [
    {
      key: SORT_ORDER.DATE,
      label:
        activeFilter === FILTER_MODE.RENEWING_SOON
          ? "Expiration Date"
          : "Last Edited",
    },
    { key: SORT_ORDER.INSURED_NAME, label: "Insured Name" },
    { key: SORT_ORDER.EFFECTIVE_DATE, label: "Effective Date" },
  ];

  useImperativeHandle(ref, () => ({
    focus: () => searchRef.current.focus(),
    updateSearchText: (text) => searchRef.current.updateText(text),
  }));

  const handleInsertQuery = (text) => {
    const current = searchRef.current;
    if (!current) return;
    current.updateText(text);
    setHelpAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: "0",
          marginTop: "0.5rem",
          padding: "0.75rem",
          borderRadius: "0.75rem",
          background: "white",
          boxShadow: "0px 4px 16px rgba(36, 41, 48, 0.1)",
          zIndex: 99,
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          sx={{ marginBottom: "12px" }}
        >
          <InlineSearch
            ref={searchRef}
            placeholder={
              "Search by insured, person, status, date, reference etc."
            }
            onChange={onChangeSearch}
            isFetching={isFetching}
            fullWidth
            sx={{ flex: 1 }}
          />
          {showHelpPopover && (
            <Tooltip title={"Search help"}>
              <IconButton
                size={"small"}
                onClick={(e) => setHelpAnchorEl(e.currentTarget)}
                sx={{ ml: 0.5 }}
              >
                <HelpOutlineIcon fontSize={"small"} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Stack
          direction={"row"}
          spacing={2}
          alignItems={"center"}
          sx={{
            width: "100%",
          }}
        >
          <FormControl size={"small"} sx={{ flex: 1 }}>
            <InputLabel>{"Show"}</InputLabel>
            <Select
              value={activeFilter}
              onChange={(e) => onChangeFilter(e.target.value)}
              label={"Show"}
              sx={{
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              <MenuItem value={FILTER_MODE.EVERYTHING}>{"Everything"}</MenuItem>
              <MenuItem value={FILTER_MODE.SAVED_BY_ME}>
                {"Saved by Me"}
              </MenuItem>
              <MenuItem value={FILTER_MODE.RENEWING_SOON}>
                {translateLabel(labels.SEARCH_FILTER_RENEWING_SOON, {
                  default: "Renewing Soon",
                })}
              </MenuItem>
              {locationConfig?.locationDropdownOptions?.length > 0 && (
                <Divider />
              )}
              {locationConfig?.locationDropdownOptions?.map((opt) => {
                const loc0 = opt.locations?.[0];
                const emoji = resolveLocationIconEmoji(
                  locationConfig.iconMap,
                  loc0
                );
                return (
                  <MenuItem
                    key={`location-${opt.label}`}
                    value={`LOCATION:${opt.label}`}
                  >
                    {emoji ? `${emoji} ${opt.label}` : opt.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size={"small"} sx={{ flex: 1 }}>
            <InputLabel>{"Across"}</InputLabel>
            <Select
              value={activeDateMode}
              onChange={(e) => onChangeDateMode(e.target.value)}
              label={"Across"}
              sx={{
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              <MenuItem value={DATE_MODE.EVERYTHING}>
                {"All Submissions"}
              </MenuItem>
              <MenuItem value={DATE_MODE.LATEST}>
                {"Latest by Insured"}
              </MenuItem>
              <MenuItem value={DATE_MODE.THIS_YEAR}>
                {"The Current Year"}
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size={"small"} sx={{ flex: 1 }}>
            <InputLabel>{"Sort"}</InputLabel>
            <Select
              value={activeSort}
              onChange={(event) => onChangeSort(event.target.value)}
              label={"Sort"}
              sx={{
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {getSortOptions().map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {showHelpPopover && (
        <SearchHelpPopover
          anchorEl={helpAnchorEl}
          open={Boolean(helpAnchorEl)}
          onClose={() => setHelpAnchorEl(null)}
          onInsertQuery={handleInsertQuery}
          guide={searchGuide}
        />
      )}
    </>
  );
});

export default SearchBar;
